import { navigate } from 'gatsby'
import { getStateEligiblity } from '@helpers/extendedServiceContract'
import { store } from '@redux/store'
import _isEmpty from 'lodash/isEmpty'
import {
  resetShippingAddress,
  setOrder,
  setShippingInvalidFields,
  setCheckoutStepsCompleted,
  setCheckoutStep,
  setDeliveryMode,
} from '@redux/modules/checkout'
import { setLocation } from '@redux/modules/location'
import { getDeliverySpecificBody } from '@helpers/checkout/delivery-section'
import { editCart } from '@services/cart'
import { fetchAddressFormat, fetchAddressLookup, updateAddress, updateDelivery } from '@services/checkout'
import { getLocation } from '@services/location'
// import { emailSignup } from '@services/email'
import { getFromBrowserStorage } from '@helpers/storage'
import { identifyUser } from '@components/integrations/Segment'
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js'
import { getOrder, checkManualAddress } from './global'
import { abbreviateState, getCurrentLocation } from '../geo-location'
import { validateEmail, validatePoBox, validateZip } from '../string-helper'
import { checkProductRegionAvailability, verifyAndUpdateCart, getCart } from '../cart'
import { setBillingAddressInfo } from './payment-section/billing-address'
import { announce, taskDone } from '../aria-announce'
import { orderHasSomeWarranty } from './review-section'

const noSaleStates = ['CA', 'AK', 'HI', 'PR', 'UT']

export const setContactInfo = (info, field = '') => {
  let order = getOrder()

  if (!field) {
    order = {
      ...order,
      contact: {
        ...info,
      },
    }
  } else if (Object.is(field, 'phone') && info?.length > 9) {
    const phoneNum = parsePhoneNumberFromString(info, 'US')
    order = {
      ...order,
      contact: {
        ...order.contact,
        [field]: phoneNum.nationalNumber,
      },
    }
  } else {
    order = {
      ...order,
      contact: {
        ...order.contact,
        [field]: info,
      },
    }
  }

  store.dispatch(setOrder(order))
}

export const setShippingAddressInfo = (info, field = '') => {
  let order = getOrder()
  if (field) {
    order = {
      ...order,
      shippingAddress: {
        ...order.shippingAddress,
        [field]: field === 'state' ? abbreviateState(info) : info,
      },
    }
  } else {
    const newInfo = { ...info }
    if (newInfo.state) newInfo.state = abbreviateState(newInfo.state)
    order = { ...order, shippingAddress: { ...order.shippingAddress, ...newInfo } }
  }

  store.dispatch(setOrder(order))
}

const isInvalidField = (field, value) => {
  switch (field) {
    case 'email':
      return !validateEmail(value)
    case 'phone':
    case 'altPhone':
      return value !== '' && !isValidPhoneNumber(value, 'US')
    case 'zip':
      return !validateZip(value)
    default:
      break
  }
  return false
}

/**
 * Determines if the Continue button should be enabled in the Checkout Shipping step based on
 * whether or not all of the required Shipping Address and Contact fields have been populated.
 * Returns true if all required fields are populated, or false if one or more is unpopulated.
 * @returns boolean
 */
export const canContinuePastShipping = () => {
  const order = getOrder()
  const shippingFields = { ...order.contact, ...order.shippingAddress }
  const requiredFields = ['address1', 'city', 'email', 'firstName', 'lastName', 'phone', 'state', 'zip']
  const requiredValues = requiredFields.map(f => shippingFields[f]?.trim())
  return requiredValues.every(f => f)
}

export const validateShippingStep = async nextStep => {
  const order = getOrder()
  const isCartV2 = !!order?.eCart?.id

  const shippingFields = { ...order.contact, ...order.shippingAddress }
  const requiredFields = ['address1', 'city', 'email', 'firstName', 'lastName', 'phone', 'state', 'zip']
  let invalidFields = []
  const { addressLookupSuccess, globalAddressId, state, zip } = shippingFields

  if (nextStep === 'payment') invalidFields = ['delivery incomplete'] // TODO - confirm how this is being used
  if (nextStep === 'review') invalidFields = ['payment incomplete'] // TODO - confirm how this is being used

  const stepOneManualEntry = getFromBrowserStorage('session', 'StepOneManualEntry')
  const mustValidate = stepOneManualEntry.toString() !== 'true'

  if (mustValidate) {
    Object.keys(shippingFields).forEach(fieldName => {
      const value = shippingFields[fieldName]

      // Check that required fields are populated
      const isUnpopulated = requiredFields.includes(fieldName) && (value?.trim() === '' || value === null)

      // Check that special fields pass basic validation
      const isInvalid = ['altPhone', 'email', 'phone', 'zip'].includes(fieldName) && isInvalidField(fieldName, value)

      if (isUnpopulated || isInvalid) {
        invalidFields.push(fieldName)
      }

      // Do not allow including PO Box in shipping address
      if ((fieldName === 'address1' || fieldName === 'address2') && !validatePoBox(value)) {
        invalidFields.push(fieldName, `${fieldName}-po-box`)
      }
    })

    // If addressLookupSuccess, then confirm a globalAddressId exists. If not addressLookupSuccess, then check the manually entered address
    if (addressLookupSuccess) {
      if (!globalAddressId) invalidFields.push('addressLookup')
    } else if (invalidFields.length === 0) {
      // get here only if none of the address fields are invalid
      invalidFields = await checkManualAddress(order) // TODO - confirm how suggested Address modal gets GAID
    }
  }

  if (invalidFields.length === 0) {
    let isValidZipCode
    let invalidZipReason = 'zip'
    if (isCartV2) {
      const zipCheck = await checkShippingZipV2(zip, order?.eCart?.id)
      isValidZipCode = zipCheck.isValid
      invalidZipReason = zipCheck.reason
    } else {
      isValidZipCode = await checkShippingZip(zip)
    }
    if (!isValidZipCode) {
      invalidFields.push(invalidZipReason)
      if (invalidZipReason !== 'zip') invalidFields.push('zip')
    } else {
      const canContinue = orderHasSomeWarranty(order) ? getStateEligiblity(state) : true
      if (!canContinue) {
        alertAndReturnToCart(
          'We apologize, but we cannot sell the furniture protection plan in the specified delivery zip code. It has been removed from your cart.',
        ) // TODO - fix this - we are not actually removing the furniture protection plan from the cart
      } else {
        try {
          // Segment Identify call
          identifyUser({
            email: order?.contact?.email,
            first_name: order?.contact?.firstName,
            zipcode: order?.shippingAddress?.zip,
            subsourcecode: 'rtgcheckout',
            signup: order?.emailCampaign ? 'true' : 'false',
            last_name: order?.contact?.lastName,
            phone_primary: order?.contact?.phone,
            phone_secondary: order?.contact?.altPhone,
            address: `${order?.shippingAddress?.address1} ${order?.shippingAddress?.address2}`,
            city: order?.shippingAddress?.city,
            state: order?.shippingAddress?.state,
            country: 'US',
          })
          // FD-2730 - NO NEED FOR CALLING emailSignup (SEGMENT COMPONENT IS HANDLING THAT)
          // if (order?.emailCampaign) {
          //   emailSignup({
          //     email: order?.contact?.email,
          //     sourcecode: 'ONLINE',
          //     sourcekey: 'ONLINE',
          //     subsourcecode: 'rtgcheckout',
          //     sourcedate: new Date().toISOString(),
          //     userid: 'rtg-web-user',
          //     zipcode: order?.shippingAddress?.zip,
          //     storenumber: '970', // 970 is the specific store number for e-commerce,
          //     signup: 'true',
          //   })
          // }
        } catch (err) {
          console.error('Error in Segment Identify call', err)
        }

        const addrOrder = await updateAddress(getAddressSpecificBody(getOrder())).catch(err => {
          console.error('Error in updateAddress call', err)
          invalidFields = ['buttonClick']
        })

        // Check if a delivery calendar exists for product. If an empty array is returned then proceed to button click
        if (addrOrder) {
          const newOrder = { ...checkOrderDeliveryDate(addrOrder), deliveryDate: order.deliveryDate }
          if (order?.expressCalendar?.includes(order.deliveryDate)) {
            store.dispatch(setOrder({ ...newOrder, deliveryDate: order.deliveryDate }))
          }
          store.dispatch(setOrder({ ...checkOrderDeliveryDate(addrOrder), acceptManual: false }))
        } else {
          invalidFields = ['buttonClick']
        }
      }
    }
  }
  store.dispatch(setShippingInvalidFields(invalidFields))
  return invalidFields
}

export const checkOrderDeliveryDate = order => {
  if (
    (order.deliveryCalendar &&
      order.deliveryCalendar[0] &&
      !order.deliveryCalendar.includes(order.deliveryDate) &&
      !order.isPickup) ||
    (order.pickupCalendar && !order.pickupCalendar.includes(order.deliveryDate) && order.isPickup)
  ) {
    const [deliveryDate] = order.deliveryCalendar
    order.deliveryDate = deliveryDate
    order.isPickup = false
  }

  return order
}

export const getAddressSpecificBody = (order, billing = false) => {
  const location = getCurrentLocation()
  const zipParts = order?.shippingAddress?.zip?.split('-')
  let body = {
    orderId: order?.orderId,
    shippingAddress: {
      ...order.shippingAddress,
      county: (location && location.county) || 'U',
      country_code: 'US',
      zip: zipParts?.[0],
      plus4: zipParts?.[1],
    },

    contact: {
      ...order.contact,
      phone: order.contact?.phone?.replace(/\D+/g, ''),
      altPhone: order.contact?.altPhone && order.contact?.altPhone?.replace(/\D+/g, ''),
    },
    deliveryTexts: order.deliveryTexts,
    emailCampaign: order.emailCampaign,
    distributionIndex: location && parseInt(location.distribution_index),
  }
  if (billing) {
    body = {
      ...body,
      billingAddress: {
        ...order.billingAddress,
        county: (location && location.county) || 'U',
        country_code: 'US',
      },
      payer: {
        ...order.payer,
      },
    }
  }
  return body
}

export const checkShippingZip = async zipIn => {
  if (!zipIn) return false
  const { response } = await getLocation(zipIn)

  if (response) {
    const { delivery_zone, price_zone, region, state } = response
    if (['California', 'Alaska', 'Hawaii', 'Utah', 'Puerto Rico'].includes(state.trim())) {
      alertLocationIssue()
      return false
    }

    const location = getCurrentLocation()
    if (
      location.zip !== zipIn ||
      location.region !== region ||
      location.delivery_zone !== delivery_zone ||
      location.price_zone !== price_zone ||
      location.state !== state
    ) {
      store.dispatch(setLocation(response))
      await verifyAndUpdateCart(getCart())
    }

    const notAvailable = await checkProductRegionAvailability(region)
    if (notAvailable.length) {
      store.dispatch(setCheckoutStepsCompleted({ shipping: false, delivery: false, payment: false }))
      alertAndReturnToCart(
        'We apologize, but some items are out of stock or unavailable for purchase in the specified zip code. Please review your cart and make changes as necessary.',
      )
    }
    return true
  }
  return false
}

/**
 * Checks if the shipping zip code is valid and available for purchase.
 * @param {string} zipIn - The shipping zip code to check.
 * @param {string} eCartId - The CartV2 cart ID.
 * @returns {Object} - An object containing isValid (boolean) and reason (string). reason is empty if isValid is true.
 */
export const checkShippingZipV2 = async (zipIn, eCartId) => {
  const zipCheck = { isValid: true, reason: '' }
  if (!zipIn) return { isValid: false, reason: '' }

  let response
  try {
    const result = await getLocation(zipIn)
    response = result.response
  } catch (error) {
    console.error('Error getting location:', error)
    return { isValid: false, reason: 'retry' }
  }

  if (response) {
    const { delivery_zone, distribution_index, price_zone, region, state } = response
    if (['California', 'Alaska', 'Hawaii', 'Utah', 'Puerto Rico'].includes(state.trim())) {
      return { isValid: false, reason: 'location' }
    }

    const location = getCurrentLocation()
    if (
      location.zip !== zipIn ||
      location.region !== region ||
      location.delivery_zone !== delivery_zone ||
      location.price_zone !== price_zone ||
      location.state !== state
    ) {
      const distributionIndex = Number.isNaN(Number(distribution_index)) ? 10 : Number(distribution_index)
      const body = {
        distributionIndex: distributionIndex || 10,
        region,
        zone: price_zone,
        zipCode: zipIn,
      }
      try {
        const newCart = await editCart(eCartId, body)
        store.dispatch(setLocation(response))
        if (!newCart?.isCartAvailable) {
          zipCheck.isValid = false
          zipCheck.reason = 'unavailable'
        }
      } catch (error) {
        console.error('Error editing cart:', error)
        zipCheck.isValid = false
        zipCheck.reason = 'retry'
      }
    }
  }
  return zipCheck
}

export const alertAndReturnToCart = (alertMsg, checkoutStep = 'shipping') => {
  // eslint-disable-next-line no-alert
  alert(alertMsg)
  store.dispatch(setCheckoutStep(checkoutStep))
  // TODO: navigation need to add code to track ga data

  navigate('/cart')
}

const getParsedAddress = (addressString = '') => {
  const [address1, line2] = addressString.split(',')
  const line2Arr = line2?.split(' ') ?? ['']

  let city = ''
  for (let i = 1; i < line2Arr.length - 2; i++) {
    city = `${city} ${line2Arr[i]}`
  }
  city = city.trim()
  const state = line2Arr[line2Arr.length - 2] ?? ''
  const [zip, plus4] = line2Arr[line2Arr.length - 1].split('-')

  return { parsed: { address1, address2: '', city, plus4, state, zip } }
}

export const setAddress = async (addressLookup, billing = false, formattedAddress = {}) => {
  if (addressLookup && addressLookup !== 'No matches.') {
    const { address1, address2, city, state, zip, plus4, globalAddressId, addressLookupSuccess } = formattedAddress
    const { parsed } = getParsedAddress(addressLookup)

    if (billing) {
      setBillingAddressInfo({
        ...parsed,
        addressLookup,
        addressLookupSuccess: true,
        globalAddressId, // TODO - why is this here? Does BillingAddr use this ID?
      })
    } else {
      setShippingAddressInfo({
        addressLookup,
        addressLookupSuccess,
        address1: address1 || parsed.address1,
        address2: address2 || parsed.address2,
        city: city || parsed.city,
        state: state || parsed.state,
        zip: zip || parsed.zip,
        plus4: plus4 || parsed.plus4,
        globalAddressId,
        showAddressLookup: false,
      }) // TODO - we include county in API calls to updateAddress - where does it come from?
    }
  } else {
    setShippingAddressInfo(false, 'showAddressLookup')
  }
}

export const onAddressLookupChange = (address, setAddressItems) => {
  const order = getOrder()
  const addressItems = []
  fetchAddressLookup(address)
    .then(data => {
      if (data.results && data.results.length > 0) {
        data.results.forEach((result, id) => {
          const gidUrl = new URL(result.format)
          const gidParams = new URLSearchParams(gidUrl.search)
          const globalAddressId = gidParams.get('id')
          addressItems.push({ globalAddressId, id, label: result.suggestion })
        })
        announceResults(data.results.length, address)
      } else {
        announceResults(0, address)
      }
      setAddressItems(addressItems)
    })
    .catch(error => {
      console.error('Error fetching address lookup:', error)
      announceResults(0, address)
      setAddressItems(addressItems)
    })
}

export const onAddressLookupSelect = async (selected, globalAddressId, showManualAddress) => {
  if (selected !== 'No matches.' && selected !== '') {
    const address = await fetchAddressFormat(globalAddressId)
    const addressArr = selected.split(',')[1].split(' ')

    const state = address?.state ?? addressArr[addressArr.length - 2]
    const formattedAddress = {
      address1: address?.address1 ?? '',
      address2: address?.address2 ?? '',
      city: address?.city ?? '',
      plus4: address?.plus4 ?? '',
      state: address.state ?? '',
      zip: address?.zip ?? '',
      globalAddressId,
      addressLookupSuccess: !!globalAddressId,
    }
    if (noSaleStates.includes(state)) {
      alertLocationIssue()
    } else {
      await setAddress(selected, false, formattedAddress)
      showManualAddress()
    }
  }
}

export const onClickChangeAddress = autocompleteApi => {
  store.dispatch(resetShippingAddress())

  const { shippingAddress, ...newAddrBody } = getAddressSpecificBody(getOrder())
  updateAddress(newAddrBody)
  taskDone(
    () => {
      if (autocompleteApi) {
        autocompleteApi._ignoreFocus = true
        autocompleteApi.refs.input.select()
      } else {
        try {
          document.getElementById('address1').focus()
        } catch (error) {}
      }
    },
    200,
    'focusAddressInput',
  )
}

export const onClickLookupAddress = (override = {}) => {
  store.dispatch(resetShippingAddress({ ...override }))
}

export const onStateChange = (newState, currentState) => {
  if (newState !== 'none') {
    if (noSaleStates.includes(newState)) return false
    if (currentState) {
      setShippingAddressInfo({
        state: newState,
        zip: '', // reset zip to '' when changing state
        addressLookup: '',
        addressLookupSuccess: false,
        globalAddressId: '',
      })
    } else {
      setShippingAddressInfo(newState, 'state')
    }
  }
  return true
}

export const onMenuVisibilityChange = (addressItems, setAddressItems) => {
  if (addressItems.length === 0) {
    setAddressItems([
      {
        id: 'no results',
        label: 'No matches.',
      },
    ])
  }
}

export const announceResults = (results, address) => {
  taskDone(
    () => {
      if (results > 0) {
        announce(`Displaying ${results + 1} Results for "${address}"`)
      } else {
        announce('No Matches. Press Tab to enter address manually.')
      }
    },
    350,
    'announceResultsCount',
  )
}

export const addressItemsFocus = (items, _autocomplete) => {
  if (items.length === 0) {
    _autocomplete._ignoreFocus = true
    _autocomplete.setState({
      highlightedIndex: null,
      isOpen: false,
    })
    _autocomplete.refs.input.select()
    _autocomplete.refs.input.setAttribute('aria-activedescendant', '')
  }
}

function alertLocationIssue() {
  // TODO - convert this to a better UI.
  // eslint-disable-next-line no-alert
  alert('We apologize, but we are currently unable to sell to Alaska, California, Hawaii, Utah or Puerto Rico.')
}

export const getCondensedShippingAddress = shippingAddress => {
  const address = { line1: '', line2: '' }
  const { address1, address2, addressLookup, addressLookupSuccess, city, state, zip } = shippingAddress

  if (addressLookupSuccess && addressLookup) {
    const [l1, l2] = addressLookup.split(',')
    address.line1 = l1
    address.line2 = l2
  } else {
    address.line1 = `${address1} ${address2 || ''}`
    address.line2 = `${city} ${abbreviateState(state)} ${zip}`
  }

  return address
}

export const goBackToShippingStep = async () => {
  // If doorway, express, or pickup delivery has been selected and then user goes back to edit
  // the Shipping section, then reset the doorway/express/pickup booleans to false and reset
  // the deliveryMode to null so that user must pick a new delivery option before going past step 2,
  // just in case the new shipping address does not support doorway/express/pickup
  const order = getOrder()
  if (order.doorwayDelivery || order.isExpress || order.isPickup) {
    const body = getDeliverySpecificBody(order)
    await updateDelivery({ ...body, doorwayDelivery: false, isExpress: false, isPickup: false })
      .then(data => {
        store.dispatch(setOrder(data))
        store.dispatch(setDeliveryMode(null))
      })
      .catch(err => {
        console.error('Error updating delivery mode:', err)
      })
  }
  return []
}
